// Growing the supplier database with Claude: suppliers for a city (from Claude's knowledge, checked by people before
// briefing), and contact emails read from the suppliers' own websites (server: dir.findSites / dir.findEmail).
// Plain functions with the Claude call and the server passed in, so they are tested on their own.
import { SUP_CATS } from './supplier-links.js';

// who each category means, for the prompt
export const SUP_WHO = { av: 'AV companies, production houses', fb: 'Caterers, venue F&B teams', dmc: 'Destination management companies',
  hos: 'Hostessing and staffing agencies', ven: 'Hotels, venues, unique spaces', dec: 'Furniture hire, decor, florists',
  prn: 'Print houses, signage, wayfinding', phv: 'Photographers, videographers', ent: 'Agencies, artists, MCs', gft: 'Gifting and merch suppliers' };
const catName = k => (SUP_CATS.find(c => c[0] === k) || [])[1] || k;

// city names compared loosely: case, accents and punctuation ignored; "Lisbon" matches "Greater Lisbon"
export const normCity = x => String(x || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
export const sameCity = (a, b) => { const x = normCity(a), y = normCity(b); return !!x && !!y && (x === y || (' ' + x + ' ').includes(' ' + y + ' ') || (' ' + y + ' ').includes(' ' + x + ' ')); };

export function finderPrompt(city, cat, n, focus, exclude) {
  const catLine = cat ? `All of them must be in this category: "${catName(cat)}" (${SUP_WHO[cat] || ''}). Use the code "${cat}" for every one.`
    : `Spread them across these categories, using each code: ${Object.keys(SUP_WHO).map(k => `"${k}" = ${catName(k)} (${SUP_WHO[k]})`).join('; ')}. Give at least 2 per category where they exist, more for AV, catering, DMC, venues and hosts.`;
  return `You are helping a senior event producer who runs corporate events for global financial-sector clients build a supplier shortlist.

List ${n} suppliers in or serving ${city} for corporate events.
${catLine}
${focus ? `Focus: ${focus}.\n` : ''}
Rules:
- Only real businesses you are confident exist and operate in or for ${city}. Never invent names. If you run out of ones you are confident about, return fewer rather than guessing.
- Prefer established suppliers that handle corporate, conference and premium client events.
- Each supplier once; no branches of the same company.
- "website": only if you are highly confident of the exact domain, otherwise "".
- "note": what they do, in at most 16 words, plain and factual. No superlatives.
- "area": neighbourhood or nearby town if they are outside the city centre or based elsewhere, otherwise "".
${exclude.length ? `- Do NOT include any of these, already in the list: ${exclude.slice(0, 250).join('; ')}\n` : ''}
Reply with only JSON, no Markdown:
{"suppliers":[{"name":"","category":"${cat || 'av'}","note":"","area":"","website":""}]}`;
}

// Claude's reply as a list of suppliers; a reply cut short keeps the complete entries
export function parseFound(text) {
  const t = String(text || '').replace(/```json|```/g, '').trim(), a = t.indexOf('{'), b = t.lastIndexOf('}');
  let arr = [];
  try { const j = JSON.parse(t.slice(a, b + 1)); arr = Array.isArray(j) ? j : (j.suppliers || []); }
  catch (_) { (t.match(/\{[^{}]*"name"[^{}]*\}/g) || []).forEach(x => { try { arr.push(JSON.parse(x)); } catch (_) { /* skip a broken entry */ } }); }
  return arr.filter(x => x && typeof x.name === 'string' && x.name.trim());
}

// Asks Claude (ask(prompt, { onText }) → { text }) until n new suppliers are found, it stops finding new ones, or
// four rounds have run. Suppliers already in the database for that city are never added twice.
// Returns the new entries, ready to save; onProgress(found so far) is called while Claude is replying.
export async function findSuppliers({ city, cat = '', n = 30, focus = '', existing = [], ask, onProgress = () => {}, newId }) {
  const known = new Set(existing.filter(d => sameCity(d.city, city)).map(d => normCity(d.name)));
  const valid = new Set(SUP_CATS.map(c => c[0]));
  const found = [];
  let rounds = 0, stalled = 0;
  while (found.length < n && rounds < 4 && stalled < 2) {
    rounds++;
    const need = Math.min(60, n - found.length + (rounds === 1 ? 5 : 8));
    const exclude = [...existing.filter(d => sameCity(d.city, city)).map(d => d.name), ...found.map(d => d.name)];
    const { text } = await ask(finderPrompt(city, cat, need, focus, exclude), { onText: ({ text }) => onProgress(found.length + (String(text).match(/"name"\s*:/g) || []).length) });
    let added = 0;
    parseFound(text).forEach(x => {
      const key = normCity(x.name);
      if (!key || known.has(key)) return;
      known.add(key);
      let c = cat || String(x.category || 'oth').toLowerCase().trim();
      if (!valid.has(c)) c = 'oth';
      let web = String(x.website || '').trim();
      if (web && !/^https?:\/\//i.test(web)) web = 'https://' + web.replace(/^\/+/, '');
      if (web && !/^https?:\/\/[^\s/]+\.[a-z]{2,}/i.test(web)) web = '';
      found.push({ id: newId(), city, area: String(x.area || '').trim().slice(0, 60), cat: c, name: x.name.trim().slice(0, 120), note: String(x.note || '').trim().slice(0, 200),
        web, email: '', phone: '', src: 'ai', focus, addedAt: Date.now() + found.length });
      added++;
    });
    stalled = added ? 0 : stalled + 1;
  }
  return found;
}

// Fills in missing emails: Claude names the websites of suppliers without one (15 at a time, kept only if the site
// names the company), then each website is read for an email, three at a time. The server saves what it finds.
// recheck: also retry suppliers checked in the last 30 days. Returns counts for the summary.
export async function findMissingEmails(entries, { call, update, onProgress = () => {}, stopped = () => false, recheck = false }) {
  const need = entries.filter(d => d && d.id && !d.email);
  const fresh = d => !recheck && d.emailCheckedAt && Date.now() - new Date(d.emailCheckedAt).getTime() < 30 * 864e5;
  const res = { emails: 0, sites: 0, none: 0, down: 0, failed: 0, noSite: 0, limited: false };
  const web = Object.fromEntries(need.map(d => [d.id, d.web || '']));
  const noWeb = need.filter(d => !d.web && !fresh(d) && !d.webCheckedAt);
  for (let i = 0; i < noWeb.length && !stopped(); i += 15) {
    const batch = noWeb.slice(i, i + 15);
    onProgress(`Finding websites… ${Math.min(i + 15, noWeb.length)} of ${noWeb.length}`);
    try {
      const r = await call('dir.findSites', { ids: batch.map(d => d.id) });
      const at = new Date().toISOString();
      await Promise.all(batch.map(d => update(d.id, { webCheckedAt: at }).catch(() => {})));
      Object.entries(r.found || {}).forEach(([id, w]) => { web[id] = w; res.sites++; });
    } catch (e) {
      if (e && (e.status === 429 || e.code === 'rate_limited' || /limit/i.test(e.message || ''))) { res.limited = true; break; }
      res.failed += batch.length;
    }
  }
  const queue = need.filter(d => web[d.id] && !fresh(d));
  const total = queue.length;
  let done = 0;
  const worker = async () => {
    while (queue.length && !stopped()) {
      const d = queue.shift();
      try {
        const r = await call('dir.findEmail', { id: d.id });
        if (r.unreachable) res.down++; else if (r.email) res.emails++; else res.none++;
      } catch (_) { res.failed++; }
      onProgress(`Reading websites for emails… ${++done} of ${total} (${res.emails} found)`);
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  res.noSite = need.filter(d => !web[d.id]).length;
  return res;
}

export function emailSummary(r, stopped) {
  return `${stopped ? 'Stopped. ' : ''}Found ${r.emails} email${r.emails === 1 ? '' : 's'}${r.sites ? ` and ${r.sites} website${r.sites === 1 ? '' : 's'}` : ''}.` +
    (r.limited ? ' Claude\'s limit was reached, so some websites weren\'t looked up; try again later.' : '') +
    (r.none ? ` ${r.none} website${r.none === 1 ? ' has' : 's have'} no email (often just a contact form).` : '') +
    (r.down ? ` ${r.down} website${r.down === 1 ? '' : 's'} didn't open; they'll be tried again next time.` : '') +
    (r.noSite ? ` ${r.noSite} supplier${r.noSite === 1 ? ' has' : 's have'} no website Claude was sure about.` : '') +
    (r.failed ? ` ${r.failed} couldn't be checked; try again later.` : '');
}
